import { Loader2, AlertTriangle, FileText } from 'lucide-react'

import { Card, CardContent } from '@/ui/card'
import { cn } from '@/core/lib/utils'
import { fmtDateTime } from '@/core/lib/formatters'

import { STATUS_STYLES, RISK_STYLES, type AgentRun } from '../lib/config'

interface AgentHistoryTabProps {
  runs: AgentRun[]
  loading: boolean
  error: string | null
}

export function AgentHistoryTab({ runs, loading, error }: AgentHistoryTabProps) {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-16 flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-slate-500">Loading action history…</p>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-red-600">{error}</p>
        </CardContent>
      </Card>
    )
  }

  if (runs.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <FileText className="w-10 h-10 text-slate-150 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No actions recorded for this agent yet.</p>
          <p className="text-xs text-slate-400 mt-1">Actions will appear here as the agent runs.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-slate-150">
              <th className="text-left font-semibold text-navy px-4 py-3 text-xs uppercase tracking-wider">Action</th>
              <th className="text-left font-semibold text-navy px-4 py-3 text-xs uppercase tracking-wider">Trigger</th>
              <th className="text-left font-semibold text-navy px-4 py-3 text-xs uppercase tracking-wider">Status</th>
              <th className="text-left font-semibold text-navy px-4 py-3 text-xs uppercase tracking-wider">Risk</th>
              <th className="text-left font-semibold text-navy px-4 py-3 text-xs uppercase tracking-wider">Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-150">
            {runs.map((run) => {
              const statusStyle = STATUS_STYLES[run.status] ?? STATUS_STYLES.pending
              const StatusIcon = statusStyle.icon
              return (
                <tr key={run.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-medium text-navy">
                      {run.actionType.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 capitalize">{run.trigger}</td>
                  <td className="px-4 py-3">
                    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border', statusStyle.badge)}>
                      <StatusIcon className="w-3 h-3" />
                      {run.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('text-xs font-semibold uppercase', RISK_STYLES[run.riskLevel] ?? 'text-gray-500')}>
                      {run.riskLevel}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDateTime(run.createdAt)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
